/**
 * This class consumes the Pipefish Timesheet API 
 * which can be used to retrieve all the necessary data and update information.
 * 
 * A JWT access token is also passed in request headers. 
 * This token is the access token acquired from login, 
 * which allows access to microsoft graph API and Pipefish Timesheet API
 */
import axios from 'axios'

export default class TimesheetApi {
    constructor(authService) {
        this.authService = authService
        this.secureApi = axios.create({
            baseURL: process.env.VUE_APP_API,
            timeout: 10000, // after 10s request timeouts
            headers: { 'Authorization': `Bearer token` } // at start no token is present
        })
    }

    /**
     * Set the token which will be used in request headers
     * @param {string} token 
     */
    setHeader(token) {
        this.secureApi.defaults.headers.Authorization = `Bearer ${token}`
    }

    /**
     * Get all the clients, projects, activities and practices 
     */
    getData() {
        return this.fetch('/data')
    }

    /**
     * Update an existing client in the dB
     * @param {object} client 
     */
    _updateClient(client) {
        const config = {
            params: {
                clientId: client.id
            }
        }
        return this.secureApi.put('/client', client, config).then(response => {
            return response.data
        })
    }

    /**
     * Update an existing client 
     * 
     * the given client object must contains fields
     * - Name : string
     * - Color : string
     * - ImageUrl : string
     * - IsActive: boolean
     * @param {object} client 
     */
    updateClient(client) {
        var self = this
        return this._updateClient(client).catch(err => {
            console.log("==timesheet Api==\nget new Token")
            self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._updateClient(client)
            })
        })
    }

    /**
     * Add a new client to the dB
     * @param {object} client 
     */
    _addClient(client) {
        return this.secureApi.post('/client', client).then(response => {
            return response.data
        })
    }

    /**
     * Add a new client
     * 
     * the given client object must contains fields
     * - Name : string
     * - Color : string
     * - ImageUrl : string
     * - IsActive: boolean
     * 
     * @param {Object} client 
     */
    addClient(client) {
        var self = this
        return this._addClient(client).catch(err => {
            self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._addClient(client)
            })
        })
    }

    /**
     * Get all projects
     */
    getProjects() {
        return this.fetch('/project')
    }

    /**
     * Update an existing project in the dB
     * @param {*} item 
     */
    _updateProject(item) {
        const config = {
            params: {
                projectId: item.project.id
            }
        }
        return this.secureApi.put('/project', {
            Name: item.project.Name,
            IsActive: item.project.IsActive,
            ClientId: item.client.id
        }, config).then(response => {
            return response.data
        })
    }

    /**
     * Update an existing project
     * @param {Object} item 
     */
    updateProject(item) {
        var self = this
        return this._updateProject(item).catch(err => {
            return self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._updateProject(item)
            })
        })
    }

    /**
     * Add a new project to the dB
     * @param {*} project 
     */
    _addProject(project) {
            return this.secureApi.post('/project', project).then(response => {
                return response.data
            })
        }
        /**
         * Add a new project
         * @param {Object} project 
         */
    addProject(project) {
        var self = this
        return this._addProject(project).catch(err => {
            return self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._addProject(project)
            })
        })
    }

    /**
     * update an exiting Practive
     * @param {*} practice 
     */
    _updatePractice(practice) {
        const config = {
            params: {
                practiceId: practice.id
            }
        }

        return this.secureApi.put('/practice', {
            Name: practice.Name,
            IsActive: practice.IsActive,
            ProjectLeadId: practice.projectLead.id,
            Code: practice.Code
        }, config).then(response => {
            return response.data
        })
    }

    /**
     * Update an existing Pipefish practice
     * @param {object} practice 
     */
    updatePractice(practice) {
        var self = this
        return this._updatePractice(practice).catch(err => {
            return self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._updatePractice(practice)
            })
        })

    }

    /**
     * add new practice to dB
     * @param {*} practice 
     */
    _addPractice(practice) {
            return this.secureApi.post('/practice', {
                Name: practice.Name,
                IsActive: practice.IsActive,
                ProjectLeadId: practice.projectLead.id,
                Code: practice.Code
            }).then(response => {
                return response.data
            })
        }
        /**
         * Add a new Pipefish practice
         * @param {Object} practice 
         */
    addPractice(practice) {
        var self = this
        return this._addPractice(practice).catch(err => {
            return self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._addPractice(practice)
            })
        })
    }

    /**
     * Get all Pipefish practices
     */
    getPractices() {
        return this.fetch('/practice')
    }

    /**
     * Get all activities
     */
    getActivities() {
        return this.fetch('/activity')
    }

    /**
     * update an existing activity in dB
     * @param {*} activity 
     */
    _updateActivity(activity) {
        var config = {
            params: {
                activityId: activity.id
            }
        }
        return this.secureApi.put('/activity', {
            Name: activity.Name,
            IsLeave: activity.IsLeave,
            IsActive: activity.IsActive,
            PracticeId: activity.practice.id,
            ProjectId: activity.project.id,
            StatementId: activity.statement.id,
            Code: activity.Code
        }, config).then(response => {
            return response.data
        })
    }

    /**
     * Update an existing activity
     * @param {Object} activity 
     */
    updateActivity(activity) {
        var self = this
        return this._updateActivity(activity).catch(err => {
            return self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._updateActivity(activity)
            })
        })
    }

    /**
     * add new activity to dB
     * @param {*} activity 
     */
    _addActivity(activity) {
            return this.secureApi.post('/activity', {
                Name: activity.Name,
                IsLeave: activity.IsLeave,
                IsActive: activity.IsActive,
                PracticeId: activity.practice.id,
                ProjectId: activity.project.id,
                StatementId: activity.statement.id,
                Code: activity.Code
            }).then(response => {
                return response.data
            })
        }
        /**
         * Add a new activity
         * @param {Object} activity 
         */
    addActivity(activity) {
        var self = this
        return this._addActivity(activity).catch(err => {
            return self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._addActivity(activity)
            })
        })
    }

    /**
     * Get all SoW
     */
    getStatements() {
        return this.fetch('/statement')
    }

    deleteStatement(statementId) {
        return this._deleteStatement(statementId);
    }

    _deleteStatement(statementId) {
        var config = {
            params: {
                statementId: statementId
            }
        }
        return this.secureApi.delete('/statement', config).then(response => {
            return response.data
        })
    }

    /**
     * Create new SoW
     * @param {object} statement 
     */
    _addStatement(statement) {
        return this.secureApi.post('/statement', statement).then(response => {
            return response.data
        })
    }

    /**
     * Create new SoW
     * @param {object} statement 
     */
    addStatement(statement) {
        var self = this
        return this._addStatement(statement).catch(err => {
            return self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._addStatement(statement)
            })
        })
    }

    /**
     * Update existing SoW
     * @param {object} statement 
     */
    _updateStatement(statement) {
        var config = {
            params: {
                statementId: statement.id
            }
        }
        return this.secureApi.put('/statement', statement, config).then(response => {
            return response.data
        })
    }

    /**
     * Update existing SoW
     * @param {object} statement 
     */
    updateStatement(statement) {
        var self = this
        return this._updateStatement(statement).catch(err => {
            return self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._updateStatement(statement)
            })
        })
    }

    /**
     * get total hours entered for SoW
     */
    getConsumedDays() {
        return this.fetch('/statement/consumed')
    }

    /**
     * Get all the users in the timesheet system
     */
    getAllUsers() {
        return this.fetch('/useraccounts')
    }

    /**
     * Get user object from DB with the given externalId
     * @param {int} externalId 
     */
    getUser(externalId) {
        var config = {
            params: {
                identifier: externalId
            }
        }
        return this.fetch('/useraccounts/me', config)
    }

    /**
     * create a new user in the dB
     * @param {*} user 
     */
    _registerUser(user) {
        return this.secureApi.post('/useraccounts/me', user).then(response => {
            return response.data
        })
    }

    /**
     * Register the user into the timesheet system 
     * (should only be done when new user logs in)
     * @param {object} user 
     */
    registerUser(user) {
        var self = this
        return this._registerUser(user).catch(err => {
            return self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._registerUser(user)
            })
        })
    }

    /**
     * Update users mobile
     * @param {int} userId id of the user
     * @param {int} mobile new mobile number
     */
    _updateMobile(userId, mobile) {
        var config = {
            params: {
                id: userId
            }
        }
        return this.secureApi.put('/useraccounts/me/mobile', { Mobile: mobile }, config).then(response => {
            return response.data
        })
    }

    /**
     * Update users mobile
     * @param {int} userId id of the user
     * @param {int} mobile new mobile number
     */
    updateMobile(userId, mobile) {
        var self = this
        return this._updateMobile(userId, mobile).catch(err => {
            return self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._updateMobile(userId, mobile)
            })
        })
    }

    /**
     * Get all the timesheet entries for every user
     * between start and end dates
     * @param {Date} start 
     * @param {Date} end 
     */
    getAllTimesheetEntries(start, end) {
        return this.getTimesheetEntries(start, end, null)
    }

    /**
     * Get entries between start and end date for
     * a single user with the given id
     * @param {Date} start 
     * @param {Date} end 
     * @param {int} id 
     */
    getTimesheetEntries(start, end, id) {
        const config = {
            params: {
                start: start,
                end: end,
                id: id
            }
        }
        return this.fetch('/timesheetentries', config)
    }

    /**
     * submit a new timesheet entry
     * @param {*} entry 
     */
    _submitEntry(entry) {
        return this.secureApi.post('/timesheetentries', entry).then(response => {
            return response.data
        })
    }

    /**
     * Submit a timesheet entry
     * @param {object} entry 
     */
    submitEntry(entry) {
        var self = this
        return this._submitEntry(entry).catch(err => {
            return self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._submitEntry(entry)
            })
        })
    }

    /**
     * Submit your saved favourite timesheet entry on all the given dates
     * 
     * @param {int} modifyId Who modified this timesheet
     * @param {int} userId id of the user
     * @param {*} dates dates to enter timesheet entry for
     */
    _submitMultipleEntries(modifyId, userId, dates) {
        var config = {
            params: {
                userId: userId,
                modifiedBy: modifyId
            }
        }

        return this.secureApi.post('/timesheetentries/week', {
            UserAccountId: userId,
            dates: dates
        }, config).then(response => {
            return response.data
        })
    }

    /**
     * Submit your saved favourite timesheet entry on all the given dates
     * 
     * @param {int} modifyId Who modified this timesheet
     * @param {int} userId id of the user
     * @param {*} dates dates to enter timesheet entry for
     */
    submitMultipleEntries(modifyId, userId, dates) {
        var self = this
        return this._submitMultipleEntries(modifyId, userId, dates).catch(err => {
            return self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._submitMultipleEntries(modifyId, userId, dates)
            })
        })
    }

    /**
     * Submit your saved favourite timesheet entry on all the given dates
     * 
     * @param {int} modifyId Who modified this timesheet
     * @param {int} userId id of the user
     * @param {*} dates dates to enter timesheet entry for
     */
    _submitLastWeekEntries(modifyId, userId, getDates, setDates) {
        var config = {
            params: {
                userId: userId,
                modifiedBy: modifyId
            }
        }

        return this.secureApi.post('/timesheetentries/lastweek', {
            UserAccountId: userId,
            getDates: getDates,
            setDates: setDates
        }, config).then(response => {
            return response.data
        })
    }

    /**
     * Submit your saved favourite timesheet entry on all the given dates
     * 
     * @param {int} modifyId Who modified this timesheet
     * @param {int} userId id of the user
     * @param {*} dates dates to enter timesheet entry for
     */
    submitLastWeekEntries(modifyId, userId, getDates, setDates) {
        var self = this
        return this._submitLastWeekEntries(modifyId, userId, getDates, setDates).catch(err => {
            return self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._submitLastWeekEntries(modifyId, userId, getDates, setDates)
            })
        })
    }

    /**
     * update an existing timesheet entry for a user
     * @param {*} entry 
     * @param {int} entryId 
     */
    _updateEntry(entry, entryId) {
        var config = {
            params: {
                entryId: entryId
            }
        }
        return this.secureApi.put('/timesheetentries', entry, config).then(response => {
            return response.data
        })
    }

    /**
     * Update an already submitted timesheet entry
     * @param {object} entry 
     * @param {int} entryId 
     */
    updateEntry(entry, entryId) {
        var self = this
        return this._updateEntry(entry, entryId).catch(err => {
            console.log("==timesheet API===\nGet new Token")
            return self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._updateEntry(entry, entryId)
            })
        })
    }

    /**
     * delete a timesheet entry for a user
     * @param {*} entryId 
     */
    _deleteEntry(entryId) {
        var config = {
            params: {
                entryId: entryId
            }
        }
        return this.secureApi.delete('/timesheetentries', config).then(response => {
            return response.data
        })
    }

    /**
     * Delete a timesheet entry with the given id
     * @param {int} entryId 
     */
    deleteEntry(entryId) {
        var self = this
        return this._deleteEntry(entryId).catch(err => {
            return self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._deleteEntry(entryId)
            })
        })
    }

    /**
     * Get recent entry for a user
     * @param {int} userId 
     */
    getRecentEntry(userId) {
        var config = {
            params: {
                userId: userId
            }
        }
        return this.fetch('/recententry', config)
    }

    /**
     * update the recent entry for a user
     * @param {*} entry 
     */
    _updateRecentEntry(entry) {
            var config = {
                params: {
                    userId: entry.UserAccountId
                }
            }
            return this.secureApi.put('/recententry', entry, config).then(response => {
                return response.data
            })
        }
        /**
         * Update the recent entry for a user
         * @param {Object} entry 
         */
    updateRecentEntry(entry) {
        var self = this
        return this._updateRecentEntry(entry).catch(err => {
            return self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._updateRecentEntry(entry)
            })
        })
    }

    /**
     * Get the requested data at `request` endpoint, with given config
     * @param {string} request 
     * @param {*} config 
     */
    _fetch(request, config) {
        return this.secureApi.get(request, config).then(response => {
            if (process.env.NODE_ENV == "development") {
                // console.log(request)
                // console.log(response.data)
            }
            return response.data
        })
    }

    /**
     * Get the requested data at `request` endpoint, with given config
     * @param {string} request
     * @param {*} config
     */
    fetch(request, config) {
        var self = this
        return this._fetch(request, config).catch(err => {
            console.log("===timesheet api===\nGet new Token")
            return self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._fetch(request, config)
            })
        })
    }

    /**
     * Update the given user's active status to the given status
     * IsAdmin and IsActive
     *
     * @param {boolean} status active status of user
     * @param {int} userId if of the user
     */
    _updateUserStatus(status, userId) {
        var config = {
            params: {
                userId: userId
            }
        }

        return this.secureApi.put('/useraccounts/update', status, config).then(response => {
            return response.data
        })
    }

    /**
     * Update the given user's active status to the given status
     * IsAdmin and IsActive
     * 
     * @param {boolean} status active status of user
     * @param {int} userId if of the user
     */
    updateUserStatus(status, userId) {
        var self = this
        return this._updateUserStatus(status, userId).catch(err => {
            return self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._updateUserStatus(status, userId)
            })
        })
    }


    /**
     * Get managers for user
     */
    getManagers() {
        return this.fetch('/manager')
    }

    /**
     * Set a user's manager
     * @param {int} userId 
     * @param {int} managerId 
     */
    _setManager(userId, managerId) {
        return this.secureApi.post('/manager', {
            UserAccountId: userId,
            ManagerId: managerId
        }).then(response => {
            return response.data
        })
    }

    /**
     * Set a user's manager
     * @param {int} userId 
     * @param {int} managerId 
     */
    setManager(userId, managerId) {
        var self = this
        return this._setManager(userId, managerId).catch(err => {
            return self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._setManager(userId, managerId)
            })
        })
    }

    /**
     * Update manager for a user
     * @param {int} linkId id in record table
     * @param {*} newIds id of new manager and user
     */
    _updateManager(linkId, newIds) {
            var config = {
                params: {
                    linkId: linkId
                }
            }
            return this.secureApi.put('/manager', newIds, config).then(response => {
                return response.data
            })
        }
        /**
         * Update manager for a user
         * @param {int} linkId id in record table
         * @param {*} newIds id of new manager and user
         */
    updateManager(linkId, newIds) {
        var self = this
        return this._updateManager(linkId, newIds).catch(err => {
            return self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._updateManager(linkId, newIds)
            })
        })
    }

    /**
     * remove manager for the given user
     * @param {int} userId id of the user to remove manager of
     */
    _removeManager(userId) {
        var config = {
            params: {
                userId: userId
            }
        }
        return this.secureApi.delete('/manager', config).then(response => {
            return response.data
        })
    }

    /**
     * remove manager for the given user
     * @param {int} userId id of the user to remove manager of
     */
    removeManager(userId) {
        var self = this
        return this._removeManager(userId).catch(err => {
            return self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._removeManager(userId)
            })
        })
    }

    /**
     * Get the logged in user's manager
     */
    getMyManager() {
        return this.fetch('/manager/me')
    }

    /**
     * Get all timesheets which require approval for the given user
     * @param {int} userId if of the user
     */
    getEntriesForApproval(userId) {
        var config = {
            params: {
                id: userId
            }
        }
        return this.fetch('/timesheetentries/approval', config)
    }

    /**
     * Submit all the given timesheet entries to manager for approval
     * @param {*} entries 
     * @param {int} id id of the user
     */
    _submitForApproval(entries, id) {
        return this.secureApi.post('/timesheetentries/approval', {
            entries: entries,
            UserAccountId: id,
            hostUrl: process.env.VUE_APP_URL
        }).then(response => {
            return response.data
        })
    }

    /**
     * Submit all the given timesheet entries to manager for approval
     * @param {*} entries timesheet entries to send for approval
     * @param {int} id id of the user
     */
    submitForApproval(entries, id) {
        var self = this
        return this._submitForApproval(entries, id).catch(err => {
            return self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._submitForApproval(entries, id)
            })
        })
    }

    /**
     * Approve timesheets based on the info given in `approveObject`
     * 
     * @param {*} approveObject object which approval information
     */
    _approveTimesheets(approveObject) {
        return this.secureApi.post('/approve', approveObject).then(response => {
            return response.data
        })
    }

    /**
     * Approve timesheets based on the info given in `approveObject`
     * @param {*} approveObject object which approval information
     */
    approveTimesheets(approveObject) {
        var self = this
        return this._approveTimesheets(approveObject).catch(err => {
            return self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._approveTimesheets(approveObject)
            })
        })
    }

    /**
     * Reject timesheets based on the info given in `approveObject`
     * @param {*} approveObject object which approval information
     */
    _rejectTimesheets(rejectObject) {
        return this.secureApi.post('/reject', rejectObject).then(response => {
            return response.data
        })
    }

    /**
     * Reject timesheets based on the info given in `approveObject`
     * @param {*} approveObject object which approval information
     */
    rejectTimesheets(rejectObject) {
        var self = this
        return this._rejectTimesheets(rejectObject).catch(err => {
            return self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._rejectTimesheets(rejectObject)
            })
        })
    }

    /**
     * Returns the automated timer information (i.e. when it's triggered ) for sending reminder emails to
     * all active users
     */
    getReminderTimer() {
        return this.fetch('/timer/reminder')
    }

    /**
     * Return the automated timer information (i.e. when its triggered) for sending emails to approvers
     * with timesheets to approve
     */
    getApprovalTimer() {
        return this.fetch('/timer/approval')
    }

    /**
     * Update the trigger time for reminding users to do their timesheets
     * 
     * @param {*} time azure function json which contains new timer schedule
     */
    _updateReminderTime(time) {
        return this.secureApi.put('/timer/reminder', time).then(response => {
            return response.data
        })
    }

    /**
     * Update the trigger time for reminding users to do their timesheets
     * 
     * @param {*} time azure function json which contains new timer schedule
     */
    updateReminderTime(time) {
        var self = this
        return this._updateReminderTime(time).catch(err => {
            return self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._updateReminderTime(time)
            })
        })
    }

    /**
     * Update the trigger time for sending emails to approvers for approving/rejecting
     * timesheets
     * 
     * @param {*} time azure function json which contins new timer schedule
     */
    _updateApprovalTime(time) {
        return this.secureApi.put('/timer/approval', time).then(response => {
            return response.data
        })
    }

    /**
     * Update the trigger time for sending emails to approvers for approving/rejecting
     * timesheets
     * 
     * @param {*} time azure function json which contins new timer schedule
     */
    updateApprovalTime(time) {
        var self = this
        return this._updateApprovalTime(time).catch(err => {
            return self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._updateApprovalTime(time)
            })
        })
    }

    /**
     * Get english description of a given cron expression
     * 
     * @param {string} cronString cron expression
     */
    getTimerDescription(cronString) {
        var config = {
            params: {
                cronString: cronString
            }
        }
        return this.fetch('/timer/description', config)
    }

    /**
     * For each user, returns all the entered timesheet entries that are
     * entered as leave
     * 
     * NOTE: Not used in the webapp. The API endpoint is for integration into excel
     * to get everyone's leave dates. (using excel's data source)
     */
    getLeaves() {
        return this.fetch('/leaves')
    }

    getLeaveCodes() {
        return this.fetch('/leaves/codes');
    }

    /**
     * If a timesheet entry is already submitted for approval or approved and contains
     * incorrect client/project/activity it will need to be unlocked to allow editing
     * 
     * This will set the timesheet back to unlocked state
     * 
     * @param {string} entryId approval_id of the timesheet entry
     */
    _resetTimesheetEntry(entryId) {
        var config = {
            params: {
                approval_id: entryId
            }
        }

        return this.secureApi.post('/reset', {}, config).then(response => {
            return response.data
        })
    }

    /**
     * If a timesheet entry is already submitted for approval or approved and contains
     * incorrect client/project/activity it will need to be unlocked to allow editing
     * 
     * This will set the timesheet back to unlocked state
     * 
     * @param {string} entryId approval_id of the timesheet entry
     */
    resetTimesheetEntry(entryId) {
        var self = this
        return this._resetTimesheetEntry(entryId).catch(err => {
            return self.authService.getMSGraphToken().then(token => {
                self.setHeader(token)
                return self._resetTimesheetEntry(entryId)
            })
        })
    }

    getTimesheetsToApprove() {
        return this.fetch('/toApprove')
    }

}